import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsightsIcon from '@mui/icons-material/Insights';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import salesService, { DynamicReportRow } from '../../../../services/salesService';
import {
  ReportSpec,
  getDimensionValueLabelKey,
  getMetricDef,
} from '../reportCatalog';

const CHART_COLORS = ['#1976D2', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#F44336', '#795548', '#607D8B', '#CDDC39'];
const OTHER_SLICE_COLOR = '#9E9E9E';

/** Dashboard aralığının hemen öncesindeki eşit uzunlukta dönem (KPI karşılaştırması için) */
const getPreviousRange = (startDate: string, endDate: string): { startDate: string; endDate: string } | null => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return null;
  const duration = end.getTime() - start.getTime();
  return {
    startDate: new Date(start.getTime() - duration - 1).toISOString(),
    endDate: new Date(start.getTime() - 1).toISOString(),
  };
};

const CardContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
}));

interface BranchLike {
  id?: string;
  branchId?: string;
  name?: string;
}

interface DynamicReportCardProps {
  spec: ReportSpec;
  startDate: string;
  endDate: string;
  branches: BranchLike[];
  onEdit?: (spec: ReportSpec) => void;
  onDelete?: (spec: ReportSpec) => void;
}

/**
 * Kullanıcı tanımlı rapor kartı: ReportSpec'e göre veriyi generic endpoint'ten çeker,
 * seçili görsel tipe göre (KPI / çubuk / pasta / çizgi / tablo) çizer.
 * Dashboard'ın tarih filtresine uyar; tarih değişince otomatik yenilenir.
 */
const DynamicReportCard: React.FC<DynamicReportCardProps> = ({
  spec,
  startDate,
  endDate,
  branches,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  const [rows, setRows] = useState<DynamicReportRow[]>([]);
  const [totalValue, setTotalValue] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  // Önceki dönem toplamı (yalnız KPI kartlarında istenir); null = veri yok/karşılaştırma kapalı
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const specKey = useMemo(
    () =>
      JSON.stringify({
        dataSource: spec.dataSource,
        metric: spec.metric,
        dimension: spec.dimension,
        timeGranularity: spec.timeGranularity,
        filters: spec.filters,
        limit: spec.limit,
        visual: spec.visual, // KPI ↔ diğer görseller geçişinde önceki dönem çağrısı değişir
      }),
    [spec]
  );

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let active = true;
    setLoading(true);
    setError(null);

    salesService
      .runDynamicReport(spec, startDate, endDate, controller.signal)
      .then((result) => {
        if (!active || controller.signal.aborted) return;
        setRows(result.data);
        setTotalValue(result.totalValue);
        setTotalCount(result.totalCount);
      })
      .catch((err: any) => {
        if (!active || controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        console.error('[DynamicReportCard] rapor yüklenemedi:', err);
        setError(err?.response?.data?.Message || err?.message || 'error');
        setRows([]);
        setTotalValue(0);
        setTotalCount(0);
      })
      .finally(() => {
        if (active && !controller.signal.aborted) setLoading(false);
      });

    // KPI kartı: önceki dönem karşılaştırması (aynı spec, kaydırılmış aralık).
    // Hata/iptal sessizce yutulur — rozet gösterilmez, kart etkilenmez.
    setPreviousValue(null);
    const previousRange = spec.visual === 'kpi' ? getPreviousRange(startDate, endDate) : null;
    if (previousRange) {
      salesService
        .runDynamicReport(spec, previousRange.startDate, previousRange.endDate, controller.signal)
        .then((result) => {
          if (!active || controller.signal.aborted) return;
          setPreviousValue(result.totalValue);
        })
        .catch(() => undefined);
    }

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, startDate, endDate]);

  const metricDef = getMetricDef(spec.dataSource, spec.metric);
  const metricFormat = metricDef?.format ?? 'currency';

  const formatValue = (value: number): string => {
    if (metricFormat === 'currency') {
      return `₺${value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (metricFormat === 'minutes') {
      return `${Math.round(value).toLocaleString(locale)} ${t('dashboard.reportBuilder.minutesShort')}`;
    }
    return Math.round(value).toLocaleString(locale);
  };

  const resolveLabel = (row: DynamicReportRow): string => {
    if (spec.dimension === 'branch') {
      const branch = branches.find((b) => (b.id || b.branchId) === row.key);
      if (branch?.name) return branch.name;
      return row.key ? row.key : t('dashboard.reportBuilder.unlabeled');
    }
    if (spec.dimension === 'time' && spec.timeGranularity === 'weekday') {
      return t(`dashboard.reportBuilder.weekdays.${row.key}`);
    }
    const labelKey = getDimensionValueLabelKey(spec.dataSource, spec.dimension, row.key);
    if (labelKey) return t(labelKey);
    if (row.label && row.label !== row.key) return row.label;
    return row.key ? row.label || row.key : t('dashboard.reportBuilder.unlabeled');
  };

  // Toplanabilir metriklerde (avg olmayanlar) top-N dışında kalanlar "Diğer" olarak eklenir.
  // Backend Totals tüm grupların birleşimini döner; fark frontend'te hesaplanır.
  const isAdditiveMetric = !spec.metric.startsWith('avg');

  const chartData = useMemo(() => {
    const data: Array<{ name: string; value: number; isOther?: boolean }> = rows.map((row) => ({
      name: resolveLabel(row),
      value: row.value,
    }));
    if (
      (spec.visual === 'pie' || spec.visual === 'bar') &&
      spec.dimension !== 'none' &&
      spec.dimension !== 'time' &&
      isAdditiveMetric &&
      data.length > 0
    ) {
      const shown = data.reduce((sum, d) => sum + d.value, 0);
      const other = totalValue - shown;
      if (other > Math.max(0.01, Math.abs(totalValue) * 0.001)) {
        data.push({ name: t('dashboard.reportBuilder.otherSlice'), value: other, isOther: true });
      }
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, totalValue, branches, i18n.resolvedLanguage, spec.visual, spec.dimension, isAdditiveMetric]);

  // Tablo görünümü için CSV dışa aktarma — tamamen frontend'te üretilir, ek endpoint yok.
  // BOM eklenir ki Excel Türkçe karakterleri doğru açsın.
  const handleExportCsv = () => {
    const escapeCsv = (value: string | number): string => {
      const str = String(value);
      return /[";\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = [
      t('dashboard.reportBuilder.tableDimension'),
      metricDef ? t(metricDef.labelKey) : t('dashboard.reportBuilder.tableValue'),
    ];
    const lines = [
      header.map(escapeCsv).join(';'),
      ...chartData.map((row) => [escapeCsv(row.name), escapeCsv(row.value)].join(';')),
      [escapeCsv(t('dashboard.reportBuilder.tableTotal')), escapeCsv(totalValue)].join(';'),
    ];
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${spec.title.replace(/[\\/:*?"<>|]/g, '_') || 'rapor'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const ChartTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 1.5, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            {item.payload?.name ?? label}
          </Typography>
          <Typography variant="body2">{formatValue(item.value)}</Typography>
        </Box>
      );
    }
    return null;
  };

  const renderBody = (): React.ReactNode => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 100 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 100, px: 1 }}>
          <Typography variant="body2" color="error" textAlign="center">
            {t('dashboard.reportBuilder.loadError')}
          </Typography>
        </Box>
      );
    }

    if (spec.visual === 'kpi') {
      // Önceki dönem karşılaştırma rozeti: prev=0 veya veri yoksa gizlenir
      const changePct =
        previousValue !== null && previousValue !== 0
          ? ((totalValue - previousValue) / Math.abs(previousValue)) * 100
          : null;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1, minHeight: 60 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight="bold" noWrap>
              {formatValue(totalValue)}
            </Typography>
            {changePct !== null && (
              <Tooltip title={t('dashboard.reportBuilder.prevPeriodTooltip', { value: formatValue(previousValue!) })}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: changePct >= 0 ? 'success.light' : 'error.light',
                    color: changePct >= 0 ? 'success.dark' : 'error.dark',
                    flexShrink: 0,
                  }}
                >
                  {changePct >= 0 ? (
                    <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                  )}
                  <Typography variant="caption" fontWeight="bold">
                    {`${Math.abs(changePct).toLocaleString(locale, { maximumFractionDigits: 1 })}%`}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {t('dashboard.reportBuilder.recordCount', { count: totalCount })}
          </Typography>
        </Box>
      );
    }

    if (chartData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 100 }}>
          <Typography variant="body2" color="textSecondary">
            {t('dashboard.chartNoData')}
          </Typography>
        </Box>
      );
    }

    if (spec.visual === 'table') {
      return (
        <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 100 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('dashboard.reportBuilder.tableDimension')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {metricDef ? t(metricDef.labelKey) : t('dashboard.reportBuilder.tableValue')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chartData.map((row, index) => (
                <TableRow key={`${row.name}_${index}`} hover>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.name}
                  </TableCell>
                  <TableCell align="right">{formatValue(row.value)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{t('dashboard.reportBuilder.tableTotal')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {formatValue(totalValue)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (spec.visual === 'pie') {
      return (
        <Box sx={{ flexGrow: 1, minHeight: 140 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={chartData} cx="50%" cy="50%" outerRadius="70%" dataKey="value" labelLine={false} label={false}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.isOther ? OTHER_SLICE_COLOR : CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<ChartTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={32}
                formatter={(value: string) => (value.length > 14 ? `${value.substring(0, 14)}...` : value)}
                wrapperStyle={{ fontSize: '11px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      );
    }

    if (spec.visual === 'line') {
      return (
        <Box sx={{ flexGrow: 1, minHeight: 140 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 10 }} width={54} tickFormatter={(v: number) => v.toLocaleString(locale)} />
              <RechartsTooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      );
    }

    // bar (varsayılan)
    return (
      <Box sx={{ flexGrow: 1, minHeight: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10 }}
              interval={0}
              tickFormatter={(value: string) => (value.length > 8 ? `${value.substring(0, 8)}...` : value)}
            />
            <YAxis tick={{ fontSize: 10 }} width={54} tickFormatter={(v: number) => v.toLocaleString(locale)} />
            <RechartsTooltip content={<ChartTooltip />} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isOther ? OTHER_SLICE_COLOR : CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <CardContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <InsightsIcon fontSize="small" color="primary" />
          <Typography variant="h6" fontWeight="bold" noWrap sx={{ fontSize: '0.95rem' }} title={spec.title}>
            {spec.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {spec.visual === 'table' && !loading && !error && chartData.length > 0 && (
            <Tooltip title={t('dashboard.reportBuilder.exportCsv')}>
              <IconButton size="small" onClick={handleExportCsv}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title={t('dashboard.reportBuilder.editReport')}>
              <IconButton size="small" onClick={() => onEdit(spec)}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t('dashboard.reportBuilder.deleteReport')}>
              <IconButton size="small" onClick={() => onDelete(spec)}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
      {renderBody()}
    </CardContainer>
  );
};

export default DynamicReportCard;
